
import { Vue, Component, Prop, VModel, Watch } from 'vue-property-decorator';
import { ROOT_ACTIONS } from '@/store/actions';
import { User } from '@/store/user/types';
import { USER_GETTERS } from '@/store/user/getters';
import { Timestamp, addDoc, collection, doc, getDoc } from 'firebase/firestore';
import { firestore } from '@/firebase';
import { parse, format, subMinutes } from 'date-fns';

@Component
export default class AddActualActivityDialog extends Vue {
  @VModel({ required: true }) show!: boolean;
  @Prop({ default: '', required: true }) activity!: string;

  get user(): User {
    return this.$store.getters[USER_GETTERS.ACTING_USER]
  }

  get isCommentRequired(): boolean {
    return this.user.type === 'COACH' || this.user.type === 'ADMIN';
  }

  get commentRules(): Array<(value: string) => boolean | string> {
    if (!this.isCommentRequired) {
      return [];
    }

    return [(value: string) => !!value?.trim() || (this.$t('commentRequired') as string)];
  }

  get shouldShowRequiresConfirmation(): boolean {
    // Always show for ADMIN users
    if (this.user.type === 'ADMIN') {
      return true;
    }
    
    // Show if activity name contains "divers" or "diverse uren" (case-insensitive)
    if (this.activityData?.name) {
      const lowerName = this.activityData.name.toLowerCase();
      return lowerName.includes('divers') || lowerName.includes('diverse uren');
    }
    
    return false;
  }

  step = 1;
  loading = false;
  activityData: any = null;

  inputs = {
    hours: 1,
    minutes: 0,
    comment: '',
    date: format(subMinutes(new Date(), new Date().getTimezoneOffset()), 'yyyy-MM-dd'),
    time: '08:00',
    participantPresent: true,
    requiresConfirmation: true
  }

  @Watch('activity', { immediate: true })
  @Watch('show')
  async fetchActivityData() {
    if (this.activity && this.show) {
      try {
        const activityDoc = await getDoc(doc(firestore, 'activities', this.activity));
        if (activityDoc.exists()) {
          this.activityData = { id: activityDoc.id, ...activityDoc.data() };
        }
      } catch (error) {
        console.error('Error fetching activity:', error);
      }
    }
  }

  resetFields() {
    this.inputs = {
      hours: 1,
      minutes: 0,
      comment: '',
      date: format(subMinutes(new Date(), new Date().getTimezoneOffset()), 'yyyy-MM-dd'),
      time: '08:00',
      participantPresent: true,
      requiresConfirmation: true
    }
  }

  async submit() {
    if (this.isCommentRequired) {
      const commentField = this.$refs.commentField as Vue & { validate: () => boolean };
      if (!commentField?.validate()) {
        return;
      }
    }

    try {
      this.loading = true;
      const pd = parse(`${this.inputs.date} ${this.inputs.time}`, 'yyyy-MM-dd HH:mm', new Date());

      // Coaches only see the checkbox for "divers" activities; all other activities
      // still require participant confirmation (same effective behaviour as admins).
      const requiresConfirmation = this.shouldShowRequiresConfirmation
        ? this.inputs.requiresConfirmation
        : true;
      // Track when the coach explicitly opted out of confirmation
      const confirmationSkipped = this.shouldShowRequiresConfirmation && !this.inputs.requiresConfirmation;

      await addDoc(collection(firestore, 'actual-activities'), {
        actualHours: parseFloat(this.inputs.hours + (this.inputs.minutes / 60) as unknown as string),
        comment: this.inputs.comment,
        activityId: this.activity,
        date: Timestamp.fromDate(pd),
        participantConfirmed: !requiresConfirmation,
        confirmationSkipped,
        skipEmail: confirmationSkipped,
        participantPresent: this.inputs.participantPresent,
      })

      this.show = false;
      this.$emit('refresh')
      this.resetFields();
      this.step = 1;

    } catch (error: any) {
      console.log(error)
      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
        color: 'error',
        text: error
      })
    } finally {
      this.loading = false;
    }
  }
}
